import { isAfter, parseISO, startOfToday } from 'date-fns';
import type { PersonEmployment } from '../models/person-employment.model';
import type { CurrentPerson } from '../models/person.model';

/**
 * Where an employment sits in its own lifetime: still to start, running today, or over.
 *
 * `isActive` alone does not answer this. The backend derives it from the end date only - and
 * deliberately so, because that is what governs access - so an approved employment that starts next
 * month is "active" by that definition. The start date is what separates the two, which is why every
 * backend "currently employed" check pairs `IsActive` with `StartDate <= today`.
 */
export type EmploymentLifecycleState = 'notYetActive' | 'active' | 'ended';

/**
 * ILOPUMA-748: an employment you applied for that starts in the future used to read as active once
 * the employer approved it, because the app went by `isActive` alone.
 */
export function employmentLifecycleState(
  employment: PersonEmployment,
  today: Date = startOfToday(),
): EmploymentLifecycleState {
  if (!employment.isActive) {
    return 'ended';
  }

  return isAfter(parseISO(employment.startDate), today) ? 'notYetActive' : 'active';
}

export function hasActiveManagerialEmploymentForOrganisation(
  person: CurrentPerson | null | undefined,
  organisationId: string | null | undefined,
): boolean {
  if (!person || !organisationId) {
    return false;
  }

  return (
    person.employments?.some(
      (employment) =>
        employment.organisationId === organisationId &&
        employment.isManagerial &&
        // Not `isActive`: an employment that has not started yet is not one you manage on today's
        // toolbox. The backend's HasActiveManagerialEmployment pairs the same two conditions, so
        // going by `isActive` alone offered actions it would then refuse.
        employmentLifecycleState(employment) === 'active',
    ) ?? false
  );
}
